import { useEffect, useMemo, useRef, useState } from "react";
import api from "../../utils/axios";
import {
  Building2, Camera, Utensils, Palette, LayoutGrid,
  Users, TrendingUp, CheckCircle2, Clock, XCircle, CalendarCheck,
  ChevronDown, ChevronUp, ChevronRight, ArrowRight, CalendarDays,
} from "lucide-react";
import { AnalyticsSkeleton } from "../../components/UI/Skeleton";
import {
  Chart,
  ArcElement,
  Tooltip,
  Legend,
  DoughnutController,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
} from "chart.js";

Chart.register(
  DoughnutController,
  ArcElement,
  Tooltip,
  Legend,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler
);

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const pct = (num, den) => (den ? Math.round((num / den) * 100) : 0);
const pad2 = (n) => String(n).padStart(2, "0");
const toISO = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const addDays = (d, n) => {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
};

const getToday = () => new Date();
const getRangeOptions = () => {
  const today = getToday();
  return [
    { label: "Today", value: "today", from: today, to: today },
    { label: "Last 7 days", value: "custom", from: addDays(today, -6), to: today },
    { label: "Last 30 days", value: "custom", from: addDays(today, -29), to: today },
    { label: "Last 90 days", value: "custom", from: addDays(today, -89), to: today },
    { label: "Custom", value: "custom", from: null, to: null },
  ];
};

const SERVICE_CONFIG = {
  venue: {
    key: "venue",
    label: "Venues",
    vendorLabel: "Business",
    itemLabel: "Venue Locations",
    color: "#9333EA",
    bg: "#FAF5FF",
    text: "#6B21A8",
    icon: Building2,
  },
  photography: {
    key: "photography",
    label: "Photography",
    vendorLabel: "Business",
    itemLabel: "Studios",
    color: "#7C3AED",
    bg: "#F5F3FF",
    text: "#5B21B6",
    icon: Camera,
  },
  caterers: {
    key: "caterers",
    label: "Catering",
    vendorLabel: "Business",
    itemLabel: "Outlets",
    color: "#DB2777",
    bg: "#FDF2F8",
    text: "#9D174D",
    icon: Utensils,
  },
  decors: {
    key: "decors",
    label: "Decors",
    vendorLabel: "Business",
    itemLabel: "Teams",
    color: "#C026D3",
    bg: "#FDF4FF",
    text: "#86198F",
    icon: Palette,
  },
};

const EMPTY_AGG = {
  total: 0,
  today: 0,
  confirmed: 0,
  pending: 0,
  cancelled: 0,
  upcoming: 0,
  revenue: 0,
};

// One colour per metric — every card, chart and legend reads from here so they stay in sync
const METRIC_COLORS = {
  total:     { color: "#9333EA", bg: "#FAF5FF" },
  today:     { color: "#DB2777", bg: "#FDF2F8" },
  confirmed: { color: "#1D9E75", bg: "#F0FDF4" },
  pending:   { color: "#D97706", bg: "#FFFBEB" },
  cancelled: { color: "#EF4444", bg: "#FEF2F2" },
  upcoming:  { color: "#7C3AED", bg: "#F5F3FF" },
};

// Revenue KPI removed — all revenue display is hidden
const KPI_META = [
  { key: "total", label: "Total Leads", icon: Users, accent: METRIC_COLORS.total.color, light: METRIC_COLORS.total.bg },
  { key: "today", label: "New Today", icon: TrendingUp, accent: METRIC_COLORS.today.color, light: METRIC_COLORS.today.bg },
  { key: "confirmed", label: "Confirmed", icon: CheckCircle2, accent: METRIC_COLORS.confirmed.color, light: METRIC_COLORS.confirmed.bg, sub: (a) => `${pct(a.confirmed, a.total)}% rate` },
  { key: "pending", label: "Pending", icon: Clock, accent: METRIC_COLORS.pending.color, light: METRIC_COLORS.pending.bg, sub: (a) => `${pct(a.pending, a.total)}% of total` },
  { key: "cancelled", label: "Cancelled", icon: XCircle, accent: METRIC_COLORS.cancelled.color, light: METRIC_COLORS.cancelled.bg, sub: (a) => `${pct(a.cancelled, a.total)}% rate` },
  { key: "upcoming", label: "Upcoming", icon: CalendarCheck, accent: METRIC_COLORS.upcoming.color, light: METRIC_COLORS.upcoming.bg },
];

// Static upcoming confirmed dates — replace with live data from API as needed
const UPCOMING_DATES = [
  { date: "Apr 14", name: "Priya & Arjun Kapoor",   service: "Main Hall + Catering", color: "#9333EA" },
  { date: "Apr 19", name: "Meena & Suresh Iyer",    service: "Lawn ceremony",        color: "#9333EA" },
  { date: "May 3",  name: "Preethi & Varun K.",     service: "Rooftop + AV",         color: "#9333EA" },
  { date: "May 11", name: "Anjali & Rajan Das",     service: "Full package",         color: "#9333EA" },
  { date: "Jun 7",  name: "Divya & Yash Mehta",    service: "Main Hall",            color: "#9333EA" },
];

const normalizeServiceKey = (serviceType) => {
  if (serviceType === "venue" || serviceType === "venues") return "venue";
  if (serviceType === "caterers" || serviceType === "catering") return "caterers";
  if (serviceType === "photography") return "photography";
  if (serviceType === "decors") return "decors";
  return null;
};

const cloneAgg = (agg) => ({
  total: agg?.total || 0,
  today: agg?.today || 0,
  confirmed: agg?.confirmed || 0,
  pending: agg?.pending || 0,
  cancelled: agg?.cancelled || 0,
  upcoming: agg?.upcoming || 0,
  revenue: agg?.revenue || 0,
});

const aggregateItems = (items = []) =>
  items.reduce(
    (acc, item) => ({
      total: acc.total + (item.leads || 0),
      today: acc.today + (item.today || 0),
      confirmed: acc.confirmed + (item.confirmed || 0),
      pending: acc.pending + (item.pending || 0),
      cancelled: acc.cancelled + (item.cancelled || 0),
      upcoming: acc.upcoming + (item.upcoming || 0),
      revenue: acc.revenue + (item.revenue || 0),
    }),
    cloneAgg(EMPTY_AGG)
  );

const aggregateVendors = (vendors = []) => aggregateItems(vendors.flatMap((vendor) => vendor.items || []));

function Avatar({ initials, color, size = 36 }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        background: `${color}18`,
        color,
        fontSize: size > 32 ? 13 : 11,
        border: `1.5px solid ${color}35`,
      }}
      className="rounded-xl flex items-center justify-center font-bold shrink-0"
    >
      {initials}
    </div>
  );
}

function LineChart({ datasets, labels, chartKey }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }
    chartRef.current = new Chart(canvasRef.current, {
      type: "line",
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            mode: "index",
            intersect: false,
            backgroundColor: "#111827",
            titleColor: "#e5e7eb",
            bodyColor: "#9ca3af",
            padding: 12,
            cornerRadius: 8,
          },
        },
        scales: {
          x: {
            ticks: { font: { size: 11 }, color: "#9ca3af", maxTicksLimit: 14, maxRotation: 0 },
            grid: { color: "rgba(0,0,0,0.04)" },
            border: { display: false },
          },
          y: {
            ticks: { font: { size: 11 }, color: "#9ca3af" },
            grid: { color: "rgba(0,0,0,0.05)" },
            border: { display: false },
          },
        },
      },
    });
    return () => chartRef.current?.destroy();
  }, [chartKey, labels, datasets]);

  return <div className="relative w-full" style={{ height: 260 }}><canvas ref={canvasRef} /></div>;
}

function DonutChart({ confirmed, pending, cancelled, newLeads, chartKey }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }
    // No data — draw a neutral, colourless ring so the chart structure still shows
    const isEmpty = !confirmed && !pending && !cancelled && !newLeads;
    chartRef.current = new Chart(canvasRef.current, {
      type: "doughnut",
      data: {
        labels: isEmpty ? ["No data"] : ["Confirmed", "Pending", "Cancelled", "New"],
        datasets: [
          {
            data: isEmpty ? [1] : [confirmed, pending, cancelled, newLeads],
            backgroundColor: isEmpty ? ["#E5E7EB"] : [METRIC_COLORS.confirmed.color, METRIC_COLORS.pending.color, METRIC_COLORS.cancelled.color, METRIC_COLORS.total.color],
            borderWidth: isEmpty ? 0 : 3,
            borderColor: "#fff",
            hoverOffset: isEmpty ? 0 : 6,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "72%",
        ...(isEmpty ? { events: [] } : {}),
        plugins: { legend: { display: false }, tooltip: { enabled: !isEmpty, backgroundColor: "#111827" } },
      },
    });
    return () => chartRef.current?.destroy();
  }, [chartKey, confirmed, pending, cancelled, newLeads]);

  const total = confirmed + pending + cancelled + newLeads;

  return (
    <div className="relative w-full" style={{ height: 160 }}>
      <canvas ref={canvasRef} />
      {/* Centre label — says what the ring is counting */}
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <span className={`font-['Manrope'] text-2xl font-extrabold tabular-nums leading-none ${total ? "text-gray-900" : "text-gray-400"}`}>{total}</span>
        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mt-1">{total ? "Total leads" : "No leads yet"}</span>
      </div>
    </div>
  );
}

function StatusStrip({ confirmed, pending, cancelled, total }) {
  const newLeads = Math.max(0, total - confirmed - pending - cancelled);
  return (
    <div className="flex h-2 rounded-full overflow-hidden gap-px">
      <div style={{ flex: confirmed, background: METRIC_COLORS.confirmed.color }} />
      <div style={{ flex: pending, background: METRIC_COLORS.pending.color }} />
      <div style={{ flex: cancelled, background: METRIC_COLORS.cancelled.color }} />
      <div style={{ flex: newLeads, background: METRIC_COLORS.total.color }} />
    </div>
  );
}

const CARD = "bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)]";
const EYEBROW = "text-[11px] font-semibold uppercase tracking-wider text-gray-400";

function EmptyCard({ icon: Icon, title, text }) {
  return (
    <div className="bg-white border border-dashed border-gray-300 rounded-2xl px-6 py-14 text-center">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-purple-50">
        <Icon size={24} className="text-purple-500" />
      </div>
      <h3 className="font-['Manrope'] text-base font-bold text-gray-900">{title}</h3>
      {text && <p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">{text}</p>}
    </div>
  );
}

function KpiGrid({ agg, upcomingOpen = false, onUpcomingClick }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-4">
      {KPI_META.map((meta) => {
        const isUpcoming = meta.key === "upcoming";
        const isActive   = isUpcoming && upcomingOpen;
        const clickable  = isUpcoming && !!onUpcomingClick;
        const sub =
          typeof meta.sub === "function"
            ? meta.sub(agg)
            : isUpcoming
            ? "Scheduled ahead"
            : "For selected period";
        const share = Math.min(100, ((agg[meta.key] || 0) / Math.max(agg.total || 1, 1)) * 100);
        return (
          <div
            key={meta.key}
            onClick={clickable ? onUpcomingClick : undefined}
            className={`group relative overflow-hidden ${CARD} p-4 sm:p-5 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-purple-900/5
              ${clickable ? "cursor-pointer select-none hover:border-purple-200" : ""}
              ${isActive ? "border-purple-400 ring-4 ring-purple-500/10" : ""}`}
          >
            {/* Accent line in the metric's colour */}
            <span style={{ background: meta.accent }} className="absolute inset-x-0 top-0 h-[3px] opacity-80" />
            <div className="flex items-start justify-between gap-2">
              <p className={EYEBROW}>{meta.label}</p>
              <div style={{ background: meta.light }} className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 -mt-1">
                <meta.icon size={17} style={{ color: meta.accent }} />
              </div>
            </div>
            <p className="mt-2 font-['Manrope'] text-3xl font-extrabold tracking-tight text-gray-900 leading-none tabular-nums">{agg[meta.key] || 0}</p>
            <div className="mt-3 h-1 rounded-full bg-gray-100 overflow-hidden">
              <div style={{ width: `${share}%`, background: meta.accent }} className="h-full rounded-full transition-all duration-1000" />
            </div>
            <p className="mt-2.5 flex items-center justify-between gap-1 text-xs font-medium text-gray-500">
              {sub}
              {clickable && (
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-gray-100 text-gray-500">
                  {isActive ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                </span>
              )}
            </p>
          </div>
        );
      })}
    </div>
  );
}

// Lead-share only — revenue bars removed
function ShareBars({ item, vendorAgg, globalAgg, vendorName }) {
  const bars = [
    { lbl: `Leads in ${vendorName}`, fill: pct(item.leads, vendorAgg.total) },
    { lbl: "Leads globally",         fill: pct(item.leads, globalAgg.total) },
  ];
  return (
    <div className="space-y-3">
      {bars.map((bar) => (
        <div key={bar.lbl}>
          <div className="flex justify-between mb-1.5">
            <span className="text-xs text-gray-500 font-medium">{bar.lbl}</span>
            <span className="text-xs font-bold tabular-nums text-gray-900">{bar.fill}%</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div style={{ width: `${bar.fill}%`, background: METRIC_COLORS.total.color }} className="h-full rounded-full transition-all duration-700" />
          </div>
        </div>
      ))}
    </div>
  );
}

// Upcoming events panel
function UpcomingDatesPanel({ dates }) {
  return (
    <div className={`${CARD} p-4 sm:p-5`}>
      <div className="flex items-center justify-between mb-3">
        <p className="font-['Manrope'] text-sm font-bold text-gray-900">Upcoming Events</p>
        <span className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs font-semibold text-gray-600 tabular-nums">{dates.length}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2 overflow-y-auto" style={{ maxHeight: 220 }}>
        {dates.map((entry, i) => {
          const [mon, day] = entry.date.split(" ");
          return (
            <div key={i} className="flex items-center gap-3 rounded-xl border border-gray-200/70 bg-gray-50/60 p-2.5">
              {/* Date tile */}
              <div className="flex h-11 w-11 flex-shrink-0 flex-col items-center justify-center rounded-xl border border-purple-100 bg-white leading-none">
                <span className="text-[9px] font-bold uppercase tracking-wider text-gray-500">{mon}</span>
                <span className="mt-0.5 font-['Manrope'] text-base font-extrabold text-gray-900 tabular-nums">{day}</span>
              </div>
              {/* Name + service */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{entry.name}</p>
                <p className="text-xs text-gray-500 mt-0.5 truncate">{entry.service}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ItemDetail({ vendor, item, vendorAgg, globalAgg }) {
  const [showUpcoming, setShowUpcoming] = useState(false);

  const newLeads      = Math.max(0, item.leads - item.confirmed - item.pending - item.cancelled);
  const confirmedPct  = pct(item.confirmed, item.leads);
  const pendingPct    = pct(item.pending,   item.leads);
  const cancelledPct  = pct(item.cancelled, item.leads);
  const newPct        = pct(newLeads,       item.leads);
  const chartKey      = `${vendor.id}-${item.id}`;

  const statCards = [
    { lbl: "Leads",     val: item.leads },
    { lbl: "Today",     val: item.today },
    { lbl: "Upcoming",  val: item.upcoming,  clickable: true },
    { lbl: "Confirmed", val: item.confirmed, pct: confirmedPct },
    { lbl: "Pending",   val: item.pending,   pct: pendingPct   },
    { lbl: "Cancelled", val: item.cancelled, pct: cancelledPct },
  ];

  return (
    <div className="flex flex-col gap-5">

      {/* Header — revenue value removed */}
      <div className="relative overflow-hidden rounded-2xl border border-gray-200/70 bg-gradient-to-br from-purple-50 via-white to-pink-50 p-4 flex items-center gap-3">
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 font-['Manrope'] text-sm font-extrabold text-white shadow-md shadow-purple-600/20">
          {vendor.initials}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-['Manrope'] text-lg font-extrabold tracking-tight text-gray-900 truncate">{item.name}</h3>
          <p className="text-xs text-gray-500 mt-0.5 font-medium truncate">{vendor.name} · {item.city}</p>
        </div>
      </div>

      {/* Stat grid — Upcoming is clickable */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {statCards.map((stat) => (
          <div
            key={stat.lbl}
            className={`rounded-xl border bg-white px-3.5 py-3 shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-all duration-150
              ${stat.clickable ? "cursor-pointer select-none hover:border-purple-300" : ""}
              ${stat.clickable && showUpcoming ? "border-purple-400 ring-4 ring-purple-500/10" : "border-gray-200/70"}`}
            onClick={stat.clickable ? () => setShowUpcoming((v) => !v) : undefined}
            title={stat.clickable ? (showUpcoming ? "Hide upcoming dates" : "Show upcoming confirmed dates") : undefined}
          >
            <p className={`mb-1 flex items-center gap-1 ${EYEBROW}`}>
              {stat.lbl}
              {stat.clickable &&
                (showUpcoming ? <ChevronUp size={12} className="text-purple-600" /> : <ChevronDown size={12} className="text-purple-600" />)}
            </p>
            <p className="font-['Manrope'] text-2xl font-extrabold tabular-nums text-gray-900">{stat.val}</p>
            {stat.pct !== undefined && (
              <p className="text-xs font-semibold mt-0.5 text-gray-400">{stat.pct}% of leads</p>
            )}
          </div>
        ))}
      </div>

      {/* ↓ Upcoming confirmed dates — slides in below stat grid */}
      {showUpcoming && <UpcomingDatesPanel dates={UPCOMING_DATES} />}

      {/* Status donut */}
      <div className="rounded-2xl border border-gray-200/70 bg-gray-50/60 p-4 sm:p-5">
        <p className="font-['Manrope'] text-sm font-bold text-gray-900">Lead Status</p>
        <p className="text-xs text-gray-500 mt-0.5 mb-3">
          {item.leads
            ? "How the leads for this listing are split by status."
            : "No leads for this listing yet — the breakdown will appear here once enquiries come in."}
        </p>
        <div className="grid grid-cols-2 gap-4 items-center">
          <DonutChart
            confirmed={item.confirmed}
            pending={item.pending}
            cancelled={item.cancelled}
            newLeads={newLeads}
            chartKey={chartKey}
          />
          <div className="space-y-2.5">
            {[
              { color: METRIC_COLORS.confirmed.color, label: "Confirmed", val: confirmedPct },
              { color: METRIC_COLORS.pending.color,   label: "Pending",   val: pendingPct   },
              { color: METRIC_COLORS.cancelled.color, label: "Cancelled", val: cancelledPct },
              { color: METRIC_COLORS.total.color,     label: "New",       val: newPct       },
            ].map((entry) => (
              <div key={entry.label} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span style={{ background: item.leads ? entry.color : "#D1D5DB" }} className="w-2.5 h-2.5 rounded-sm shrink-0" />
                  <span className="text-xs text-gray-500 font-medium">{entry.label}</span>
                </div>
                <span className={`text-xs font-bold tabular-nums ${item.leads ? "text-gray-900" : "text-gray-400"}`}>{entry.val}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Market Share — leads only, no revenue bars */}
      <div className="rounded-2xl border border-gray-200/70 bg-gray-50/60 p-4 sm:p-5">
        <p className="font-['Manrope'] text-sm font-bold text-gray-900 mb-3">Market Share</p>
        <ShareBars item={item} vendorAgg={vendorAgg} globalAgg={globalAgg} vendorName={vendor.name} />
      </div>

    </div>
  );
}

function ServicePanel({ serviceKey, serviceData }) {
  const config  = SERVICE_CONFIG[serviceKey];
  const vendors = (serviceData?.vendors || []).map((vendor) => ({
    ...vendor,
    color: vendor.color || config.color,
  }));
  const [curVendorIdx,   setCurVendorIdx]   = useState(0);
  const [curItemIdx,     setCurItemIdx]     = useState(0);
  const [upcomingOpen,   setUpcomingOpen]   = useState(false);

  useEffect(() => {
    setCurVendorIdx(0);
    setCurItemIdx(0);
    setUpcomingOpen(false);
  }, [serviceKey, vendors.length]);

  if (!vendors.length) {
    return (
      <EmptyCard icon={config.icon} title={`No ${config.label.toLowerCase()} data found for this period.`} text="Try a longer period from the Overview tab." />
    );
  }

  const vendor    = vendors[Math.min(curVendorIdx, vendors.length - 1)];
  const vendorItems = vendor?.items || [];
  const item      = vendorItems[Math.min(curItemIdx, Math.max(vendorItems.length - 1, 0))];
  const vendorAgg = aggregateItems(vendorItems);
  const globalAgg = aggregateVendors(vendors);

  if (!item) {
    return (
      <EmptyCard icon={config.icon} title={`No ${config.itemLabel.toLowerCase()} available yet.`} />
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <KpiGrid
        agg={cloneAgg(serviceData.agg || EMPTY_AGG)}
        upcomingOpen={upcomingOpen}
        onUpcomingClick={() => setUpcomingOpen((v) => !v)}
      />

      {upcomingOpen && <UpcomingDatesPanel dates={UPCOMING_DATES} />}

      <div className="flex flex-wrap items-center gap-1.5 text-xs font-semibold">
        <span className="rounded-lg px-2.5 py-1 text-gray-500">All {config.label}</span>
        <ChevronRight size={14} className="text-gray-300" />
        <span className="rounded-lg border border-gray-200/70 bg-white px-2.5 py-1 text-gray-700">{vendor.name}</span>
        <ChevronRight size={14} className="text-gray-300" />
        <span className="rounded-lg border border-purple-200 bg-purple-50 px-2.5 py-1 text-gray-900">{item.name}</span>
      </div>

      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-[260px_300px_minmax(0,1fr)] items-start">

        {/* Vendor list */}
        <div className="bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)] overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <p className="font-['Manrope'] text-sm font-bold text-gray-900">{config.vendorLabel}</p>
            <span className="rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[11px] font-semibold text-gray-600 tabular-nums">{vendors.length}</span>
          </div>
          <div style={{ maxHeight: 560 }} className="overflow-y-auto p-2 space-y-1">
            {vendors.map((entry, index) => {
              const isActive  = index === curVendorIdx;
              const entryAgg  = aggregateItems(entry.items || []);
              return (
                <button
                  key={entry.id}
                  onClick={() => { setCurVendorIdx(index); setCurItemIdx(0); }}
                  aria-pressed={isActive}
                  className={`relative w-full flex items-center gap-3 rounded-xl px-3 py-3 text-left transition-all ${isActive ? "bg-purple-50 ring-1 ring-inset ring-purple-200" : "hover:bg-gray-50"}`}
                >
                  <Avatar initials={entry.initials} color={entry.color} size={36} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-900 truncate">{entry.name}</p>
                    <p className="text-xs text-gray-500 mt-0.5 font-medium">{(entry.items || []).length} locations · {entryAgg.total} leads</p>
                  </div>
                  <ChevronRight size={16} className={`shrink-0 ${isActive ? "text-purple-600" : "text-gray-300"}`} />
                </button>
              );
            })}
          </div>
        </div>

        {/* Item list */}
        <div className="bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)] overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <p className="font-['Manrope'] text-sm font-bold text-gray-900">{config.itemLabel}</p>
            <span className="rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[11px] font-semibold text-gray-600 tabular-nums">{vendorItems.length}</span>
          </div>
          <div style={{ maxHeight: 560 }} className="overflow-y-auto p-2 space-y-1">
            {vendorItems.map((entry, index) => {
              const isActive = index === curItemIdx;
              return (
                <button
                  key={entry.id}
                  onClick={() => setCurItemIdx(index)}
                  aria-pressed={isActive}
                  className={`w-full rounded-xl px-3 py-3 text-left transition-all ${isActive ? "bg-purple-50 ring-1 ring-inset ring-purple-200" : "hover:bg-gray-50"}`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-gray-900 truncate">{entry.name}</p>
                      <span className="rounded-md bg-white border border-gray-200/70 px-1.5 py-0.5 text-[11px] font-bold text-gray-700 tabular-nums shrink-0">{entry.leads}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 font-medium">{entry.city}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Leads</span>
                      <div className="flex-1">
                        <StatusStrip confirmed={entry.confirmed} pending={entry.pending} cancelled={entry.cancelled} total={entry.leads} />
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Detail panel */}
        <div className="bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)] p-5 overflow-y-auto" style={{ maxHeight: 640 }}>
          <ItemDetail
            key={`${serviceKey}-${vendor.id}-${item.id}`}
            vendor={vendor}
            item={item}
            vendorAgg={vendorAgg}
            globalAgg={globalAgg}
          />
        </div>

      </div>
    </div>
  );
}

function OverallPanel({
  accessibleServices,
  analyticsData,
  rangeOptions,
  rangeIdx,
  setRangeIdx,
  customFrom,
  setCustomFrom,
  customTo,
  setCustomTo,
  svcFilter,
  setSvcFilter,
}) {
  const [upcomingOpen, setUpcomingOpen] = useState(false);

  const isCustom       = rangeIdx === rangeOptions.length - 1;
  const visibleServices = svcFilter === "all" ? accessibleServices : [svcFilter];
  const trend          = analyticsData?.trend || [];
  const labels         = trend.map((entry) => entry.label);
  const chartKey       = `${rangeIdx}-${customFrom}-${customTo}-${svcFilter}-${labels.length}`;

  const lineDatasets = visibleServices.map((key) => ({
    label: SERVICE_CONFIG[key].label,
    data: trend.map((entry) => entry[key]?.leads || 0),   // always leads
    borderColor:     SERVICE_CONFIG[key].color,
    backgroundColor: `${SERVICE_CONFIG[key].color}14`,
    tension: 0.4,
    fill: true,
    pointRadius:      trend.length > 20 ? 0 : 3,
    pointBackgroundColor: "#fff",
    pointHoverRadius: 6,
    borderWidth: 2.5,
  }));

  const perServiceAgg = accessibleServices.map((key) => ({
    key,
    cfg:         SERVICE_CONFIG[key],
    agg:         cloneAgg(analyticsData?.services?.[key]?.agg || EMPTY_AGG),
    vendorCount: analyticsData?.services?.[key]?.vendorCount || 0,
  }));

  const Pill = ({ active, onClick, children }) => (
    <button
      onClick={onClick}
      aria-pressed={active}
      className={`text-xs px-3 h-8 rounded-lg font-semibold whitespace-nowrap transition-all duration-150 ${active ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md shadow-purple-600/20" : "text-gray-500 hover:bg-gray-50 hover:text-gray-900"}`}
    >
      {children}
    </button>
  );

  const periodLeads = lineDatasets.reduce((sum, ds) => sum + ds.data.reduce((x, y) => x + y, 0), 0);

  return (
    <div className="flex flex-col gap-5">

      {/* Filters — Revenue metric pill removed */}
      <div className={`${CARD} flex flex-col xl:flex-row xl:items-center gap-4 p-4`}>
        <div className="min-w-0">
          <p className={`mb-1.5 ${EYEBROW}`}>Period</p>
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex max-w-full gap-1 overflow-x-auto rounded-xl border border-gray-200/70 p-1">
              {rangeOptions.map((range, index) => (
                <Pill key={range.label} active={rangeIdx === index} onClick={() => setRangeIdx(index)}>
                  {range.label}
                </Pill>
              ))}
            </div>
            {isCustom && (
              <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 h-10">
                <input type="date" aria-label="From date" value={customFrom} max={customTo} onChange={(e) => setCustomFrom(e.target.value)} className="text-xs font-medium bg-transparent text-gray-900 outline-none" />
                <ArrowRight size={12} className="text-gray-300" />
                <input type="date" aria-label="To date" value={customTo} min={customFrom} max={toISO(getToday())} onChange={(e) => setCustomTo(e.target.value)} className="text-xs font-medium bg-transparent text-gray-900 outline-none" />
              </div>
            )}
          </div>
        </div>
        <div className="min-w-0">
          <p className={`mb-1.5 ${EYEBROW}`}>Service</p>
          <div className="inline-flex max-w-full gap-1 overflow-x-auto rounded-xl border border-gray-200/70 p-1">
            <Pill active={svcFilter === "all"} onClick={() => setSvcFilter("all")}>All</Pill>
            {accessibleServices.map((key) => (
              <Pill key={key} active={svcFilter === key} onClick={() => setSvcFilter(key)}>
                {SERVICE_CONFIG[key].label}
              </Pill>
            ))}
          </div>
        </div>
        <span className="xl:ml-auto inline-flex items-center gap-2 self-start xl:self-end rounded-xl border border-gray-200/70 bg-gray-50/60 px-3 h-10 text-xs font-semibold text-gray-700 tabular-nums">
          <CalendarDays size={14} className="text-purple-500" />
          {analyticsData?.range?.startDate || customFrom}
          <ArrowRight size={12} className="text-gray-300" />
          {analyticsData?.range?.endDate || customTo}
        </span>
      </div>

      <KpiGrid
        agg={cloneAgg(analyticsData?.overview || EMPTY_AGG)}
        upcomingOpen={upcomingOpen}
        onUpcomingClick={() => setUpcomingOpen((v) => !v)}
      />

      {upcomingOpen && <UpcomingDatesPanel dates={UPCOMING_DATES} />}

      {/* Leads trend chart — Revenue Ranking panel removed */}
      <div className={`${CARD} p-5 sm:p-6`}>
        <div className="flex flex-wrap gap-4 justify-between items-start mb-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 shadow-md shadow-purple-600/20">
              <TrendingUp size={18} className="text-white" />
            </div>
            <div>
              <h3 className="font-['Manrope'] text-base font-bold text-gray-900">Leads over time</h3>
              <p className="text-xs text-gray-500 mt-0.5">Daily trend · selected services</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-xl border border-gray-200/70 bg-gray-50/60 px-3 py-1.5 text-xs font-medium text-gray-500">
              <span className="font-['Manrope'] text-sm font-extrabold text-gray-900 tabular-nums">{periodLeads}</span> leads in period
            </span>
            {visibleServices.map((key) => (
              <span key={key} className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs font-semibold text-gray-600">
                <span style={{ background: SERVICE_CONFIG[key].color }} className="w-2 h-2 rounded-full" />
                {SERVICE_CONFIG[key].label}
              </span>
            ))}
          </div>
        </div>
        {labels.length ? (
          <LineChart datasets={lineDatasets} labels={labels} chartKey={chartKey} />
        ) : (
          <div className="h-[260px] flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 text-center">
            <p className="text-sm font-semibold text-gray-900">No trend data for this period.</p>
            <p className="text-sm text-gray-500 mt-1">Pick a longer period to see how leads move over time.</p>
          </div>
        )}
      </div>

      {/* Service Breakdown — revenue stat replaced with Cancelled */}
      <div>
        <h3 className="font-['Manrope'] text-base font-bold text-gray-900 mb-4">Service breakdown</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {perServiceAgg
            .filter((entry) => svcFilter === "all" || entry.key === svcFilter)
            .map((entry) => (
              <div key={entry.key} className={`${CARD} p-5 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-purple-900/5 hover:border-purple-200 transition-all duration-200`}>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center shadow-md shadow-purple-600/20 flex-shrink-0">
                    <entry.cfg.icon size={20} className="text-white" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-['Manrope'] text-base font-bold text-gray-900 truncate">{entry.cfg.label}</p>
                    <p className="text-xs text-gray-500 font-medium truncate">{entry.vendorCount} business · {analyticsData?.services?.[entry.key]?.itemCount || 0} items</p>
                  </div>
                </div>

                <div className="mt-5 flex items-end justify-between gap-3">
                  <div>
                    <p className={EYEBROW}>Total leads</p>
                    <p className="mt-1 font-['Manrope'] text-3xl font-extrabold tracking-tight text-gray-900 tabular-nums leading-none">{entry.agg.total}</p>
                  </div>
                  <span className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-semibold text-gray-600 tabular-nums">
                    {pct(entry.agg.confirmed, entry.agg.total)}% confirmed
                  </span>
                </div>

                <div className="mt-4">
                  <StatusStrip confirmed={entry.agg.confirmed} pending={entry.agg.pending} cancelled={entry.agg.cancelled} total={entry.agg.total} />
                </div>

                <div className="mt-4 grid grid-cols-3 gap-2">
                  {[
                    { lbl: "Confirmed", val: entry.agg.confirmed, c: METRIC_COLORS.confirmed.color },
                    { lbl: "Pending",   val: entry.agg.pending,   c: METRIC_COLORS.pending.color },
                    { lbl: "Cancelled", val: entry.agg.cancelled, c: METRIC_COLORS.cancelled.color },
                  ].map((stat) => (
                    <div key={stat.lbl} className="rounded-xl border border-gray-200/70 bg-gray-50/60 px-2.5 py-2.5">
                      <p className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500">
                        <span style={{ background: stat.c }} className="w-1.5 h-1.5 rounded-full" />
                        {stat.lbl}
                      </p>
                      <p className="mt-1 font-['Manrope'] text-lg font-extrabold tabular-nums text-gray-900 leading-none">{stat.val}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      </div>

    </div>
  );
}

export default function AnalyticsSection({ vendorData, services = [] }) {
  const accessibleServices = useMemo(() => {
    const unique = new Set();
    services.forEach((service) => {
      const key = normalizeServiceKey(service?.serviceType);
      if (key && SERVICE_CONFIG[key]) unique.add(key);
    });
    return Array.from(unique);
  }, [services]);

  const [activeTab,   setActiveTab]   = useState("overall");
  const [rangeIdx,    setRangeIdx]    = useState(1);
  const [customFrom,  setCustomFrom]  = useState(() => toISO(addDays(getToday(), -6)));
  const [customTo,    setCustomTo]    = useState(() => toISO(getToday()));
  const [svcFilter,   setSvcFilter]   = useState("all");
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState("");

  const rangeOptions = useMemo(() => getRangeOptions(), []);

  const queryParams = useMemo(() => {
    const selected = rangeOptions[rangeIdx];
    if (!selected || selected.label === "Custom") {
      return { range: "custom", start_date: customFrom, end_date: customTo };
    }
    if (selected.value === "today") return { range: "today" };
    return { range: "custom", start_date: toISO(selected.from), end_date: toISO(selected.to) };
  }, [rangeIdx, customFrom, customTo]);

  useEffect(() => {
    if (!accessibleServices.length) { setLoading(false); return; }
    let ignore = false;
    const loadAnalytics = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await api.get("/api/vendor/analytics/dashboard", { params: queryParams });
        if (!ignore && response.data?.success) setAnalyticsData(response.data.data);
      } catch (err) {
        if (!ignore) setError(err?.response?.data?.message || "Failed to load analytics data");
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    loadAnalytics();
    return () => { ignore = true; };
  }, [accessibleServices.length, queryParams]);

  useEffect(() => {
    if (activeTab !== "overall" && !accessibleServices.includes(activeTab)) setActiveTab("overall");
  }, [activeTab, accessibleServices]);

  if (!accessibleServices.length) {
    return <EmptyCard icon={LayoutGrid} title="No services assigned." text="Contact your administrator." />;
  }

  const tabs = [
    { key: "overall", label: "Overview", icon: LayoutGrid },
    ...accessibleServices.map((key) => ({ key, label: SERVICE_CONFIG[key].label, icon: SERVICE_CONFIG[key].icon })),
  ];

  const activeConfig      = activeTab !== "overall" ? SERVICE_CONFIG[activeTab] : null;
  const activeServiceData = activeTab !== "overall" ? analyticsData?.services?.[activeTab] : null;
  const vendorName        = vendorData?.vendor?.business_name || vendorData?.vendor?.contactDetails?.name || "Your business";
  const totalBusinesses   = accessibleServices.reduce((sum, key) => sum + (analyticsData?.services?.[key]?.vendorCount || 0), 0);
  const totalItems        = accessibleServices.reduce((sum, key) => sum + (analyticsData?.services?.[key]?.itemCount  || 0), 0);

  const BannerIcon = activeConfig?.icon || TrendingUp;

  return (
    <div className="w-full space-y-5">
      {/* Banner */}
      <div className="relative overflow-hidden rounded-2xl border border-gray-200/70 bg-gradient-to-br from-purple-50 via-white to-pink-50 shadow-[0_1px_3px_rgba(16,24,40,0.04)] p-6 sm:p-7">
        <div className="pointer-events-none absolute -top-20 -right-16 h-56 w-56 rounded-full bg-purple-200/40 blur-3xl" />
        <div className="relative flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="w-12 h-12 flex-shrink-0 rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 shadow-md shadow-purple-600/20 flex items-center justify-center">
            <BannerIcon size={24} className="text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="font-['Manrope'] text-xl sm:text-2xl font-extrabold text-gray-900 tracking-tight">
              {activeTab === "overall" ? "Business Overview" : `${activeConfig?.label} Analytics`}
            </h1>
            <p className="text-sm text-gray-600 mt-1 truncate">{vendorName}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {(activeTab === "overall"
              ? [
                  { value: accessibleServices.length, label: "Services" },
                  { value: totalBusinesses, label: "Business" },
                  { value: totalItems, label: "Items" },
                ]
              : [
                  { value: activeServiceData?.vendorCount || 0, label: "Business" },
                  { value: activeServiceData?.itemCount || 0, label: "Items" },
                ]
            ).map((stat) => (
              <div key={stat.label} className="rounded-xl border border-gray-200/70 bg-white px-4 py-2 text-center">
                <p className="font-['Manrope'] text-lg font-extrabold text-gray-900 tabular-nums leading-none">{stat.value}</p>
                <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs — pinned under the top bar */}
      <div className="inline-flex max-w-full items-center gap-1 overflow-x-auto p-1 sticky top-20 z-10 rounded-xl bg-white border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)]">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all duration-150 ${isActive ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md shadow-purple-600/20" : "text-gray-500 hover:text-gray-900 hover:bg-gray-50"}`}
            >
              <tab.icon size={14} />
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="w-full">
        {loading ? (
          <AnalyticsSkeleton />
        ) : error ? (
          <div className="bg-red-50/60 border border-red-200 rounded-2xl px-6 py-12 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-white border border-red-200">
              <XCircle size={22} className="text-red-500" />
            </div>
            <p className="font-['Manrope'] text-base font-bold text-gray-900 mb-1">{error}</p>
            <p className="text-sm text-gray-600">Make sure the updated analytics route is running and reachable from this dashboard.</p>
          </div>
        ) : activeTab === "overall" ? (
          <OverallPanel
            accessibleServices={accessibleServices}
            analyticsData={analyticsData}
            rangeOptions={rangeOptions}
            rangeIdx={rangeIdx}
            setRangeIdx={setRangeIdx}
            customFrom={customFrom}
            setCustomFrom={setCustomFrom}
            customTo={customTo}
            setCustomTo={setCustomTo}
            svcFilter={svcFilter}
            setSvcFilter={setSvcFilter}
          />
        ) : (
          <ServicePanel serviceKey={activeTab} serviceData={activeServiceData} />
        )}
      </div>
    </div>
  );
}
